/** Localised words for a desktop notification / push preview. */
export interface NotificationLabels {
  newMessage: string;
  image: string;
  video: string;
  voiceMessage: string;
  attachment: string;
}

/** Render a desktop-notification body from a (possibly Tiptap-HTML)
 *  message + its attachments. The Notification API treats `body` as
 *  plain text — raw HTML would show literally — so route the message
 *  through DOMParser for proper entity decoding + tag stripping.
 *  Falls back to a (localised) media marker when the visible text is empty. */
export function htmlToPlainText(
  html: string,
  media: { mimetype: string }[],
  labels: NotificationLabels,
): string {
  let text = "";
  if (html) {
    try {
      // DOMParser is the safest entity-aware HTML → text path. We don't
      // attach the document to the live DOM — just extract textContent.
      const doc = new DOMParser().parseFromString(html, "text/html");
      text = (doc.body.textContent ?? "").replace(/\s+/g, " ").trim();
    } catch {
      // Defensive: fall back to a regex strip if DOMParser is somehow
      // unavailable (very old browsers / sandboxed contexts).
      text = html
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    }
  }
  if (text) return text;
  if (media.length === 0) return labels.newMessage;
  const head = media[0]?.mimetype ?? "";
  const kind = head.startsWith("image/")
    ? labels.image
    : head.startsWith("video/")
      ? labels.video
      : head.startsWith("audio/")
        ? labels.voiceMessage
        : labels.attachment;
  return media.length > 1 ? `${kind} +${media.length - 1}` : kind;
}
